import { addDays, dayIndexOf, formatCalendarDate, isValidCalendarDate, parseCalendarDate, toDashed, weekdayIndexOf } from '@lib/datetime'

/**
 * The trip calendar: an ordinary month calendar, Sunday-first, with a page for
 * each month the trip has days in and nothing to scroll. An undated trip pages
 * through rows of day-number chips instead. Pure and UTC-stable, like every
 * calendar-date rule.
 *
 * Deliberately not the iOS day view's continuous trip-weeks list: the web card
 * has the room for whole months, and a month reads at a glance.
 */

/** How the trip's days are painted: the picked one, the ones holding events, and each day's destination colour. */
export interface DayPaint {
	selectedDay: number
	/** Zero-based days holding at least one event: they get the mark under the number. */
	daysWithEvents: ReadonlySet<number>
	colorIndexOf: (dayIndex: number) => number
}

/** A trip day: the one kind of cell that is a control. */
export interface TripDayCell {
	kind: 'day'
	key: string
	dayIndex: number
	/** The day of the month, or the day number on an undated trip. */
	label: number
	/** "Oct 3, 2026", or "Day 3" on an undated trip. */
	name: string
	colorIndex: number
	hasEvents: boolean
	selected: boolean
}

export type CalendarCell =
	/** A column held open for a day of the neighbouring month, so the weekdays stay aligned. */
	| { kind: 'blank'; key: string }
	/** A day of the month outside the trip: a plain number, never a control. */
	| { kind: 'outside'; key: string; label: number }
	| TripDayCell

export interface CalendarPage {
	/** Stable across re-paints: the month ("2026-10"), or an undated page's first day. */
	key: string
	/** "Oct 2026", or "Days 1–35" on an undated trip. */
	title: string
	weeks: CalendarCell[][]
}

export interface TripCalendarModel {
	/** False on an undated trip, whose pages are day-number chips under no weekday header. */
	dated: boolean
	pages: CalendarPage[]
	selectedDay: number
	/** The page holding the selected day — the one the calendar turns to. */
	selectedPage: number
}

/** An undated trip's page: five rows of seven chips, about what a month shows. */
const CHIP_ROWS_PER_PAGE = 5

const monthKey = (date: string): string => date.slice(0, 7)

const nextMonthKey = (month: string): string => {
	const [year, monthNumber] = month.split('-').map(Number)
	return monthNumber === 12 ? `${year + 1}-01` : `${year}-${String(monthNumber + 1).padStart(2, '0')}`
}

/** The months from the trip's first day to its last, as "YYYY-MM". */
export const tripMonths = (startDate: string | null | undefined, dayCount: number): string[] => {
	if (!isValidCalendarDate(startDate) || dayCount <= 0) return []
	const start = toDashed(startDate)
	const lastMonth = monthKey(addDays(start, dayCount - 1))
	const months: string[] = []
	for (let month = monthKey(start); month <= lastMonth; month = nextMonthKey(month)) months.push(month)
	return months
}

/** The Sunday each week touching `month` starts on. */
export const monthWeekStarts = (month: string): string[] => {
	const first = `${month}-01`
	const nextFirst = `${nextMonthKey(month)}-01`
	const starts: string[] = []
	for (let weekStart = addDays(first, -(weekdayIndexOf(first) ?? 0)); weekStart < nextFirst; weekStart = addDays(weekStart, 7)) {
		starts.push(weekStart)
	}
	return starts
}

/** An undated trip's stand-in for the calendar: rows of up to 7 zero-based day indices. */
export const chipRows = (dayCount: number): number[][] => {
	const rows: number[][] = []
	for (let first = 0; first < dayCount; first += 7) {
		rows.push(Array.from({ length: Math.min(7, dayCount - first) }, (_, i) => first + i))
	}
	return rows
}

/** Everything the trip calendar draws: a page per month (or per run of an undated trip's chips), each trip day already painted. */
export const tripCalendar = (
	startDate: string | null | undefined,
	dayCount: number,
	{ selectedDay, daysWithEvents, colorIndexOf }: DayPaint,
): TripCalendarModel => {
	const dayCell = (key: string, dayIndex: number, label: number, name: string): CalendarCell => ({
		kind: 'day',
		key,
		dayIndex,
		label,
		name,
		colorIndex: colorIndexOf(dayIndex),
		hasEvents: daysWithEvents.has(dayIndex),
		selected: dayIndex === selectedDay,
	})
	const pageOf = (index: number, pageCount: number): number => Math.min(Math.max(0, index), Math.max(0, pageCount - 1))

	const months = tripMonths(startDate, dayCount)
	if (months.length === 0) {
		const rows = chipRows(dayCount)
		const pages: CalendarPage[] = []
		for (let first = 0; first < rows.length; first += CHIP_ROWS_PER_PAGE) {
			const weeks = rows.slice(first, first + CHIP_ROWS_PER_PAGE)
			const firstDay = weeks[0][0] + 1
			const lastDay = (weeks[weeks.length - 1].at(-1) ?? weeks[0][0]) + 1
			pages.push({
				key: `days-${firstDay}`,
				title: firstDay === lastDay ? `Day ${firstDay}` : `Days ${firstDay}–${lastDay}`,
				weeks: weeks.map(row =>
					row.map(dayIndex => dayCell(`day-${dayIndex}`, dayIndex, dayIndex + 1, `Day ${dayIndex + 1}`)),
				),
			})
		}
		const selectedPage = pageOf(Math.floor(selectedDay / (7 * CHIP_ROWS_PER_PAGE)), pages.length)
		return { dated: false, pages, selectedDay, selectedPage }
	}

	const start = toDashed(startDate)
	const pages: CalendarPage[] = months.map(month => ({
		key: month,
		title: formatCalendarDate(`${month}-01`, 'monthYear'),
		weeks: monthWeekStarts(month).map(weekStart =>
			Array.from({ length: 7 }, (_, offset): CalendarCell => {
				const date = addDays(weekStart, offset)
				if (monthKey(date) !== month) return { kind: 'blank', key: date }
				const dayOfMonth = parseCalendarDate(date)?.day ?? 0
				const dayIndex = dayIndexOf(date, start)
				if (dayIndex < 0 || dayIndex >= dayCount) return { kind: 'outside', key: date, label: dayOfMonth }
				return dayCell(date, dayIndex, dayOfMonth, formatCalendarDate(date, 'medium'))
			}),
		),
	}))
	// The last month at or before the selected day's, so a selection off either end still lands on a page.
	const selectedMonth = monthKey(addDays(start, selectedDay))
	const selectedPage = pageOf(months.filter(month => month <= selectedMonth).length - 1, pages.length)
	return { dated: true, pages, selectedDay, selectedPage }
}
